// The fetch wrapper every typed api/ module is built on.
//
// It exists because `06` §1.6 locks auto-REST (nil API rules on every
// collection) and makes each access path an explicit Go route: the `/v1/*`
// resource API and the `/api/cloud/auth/*` security plane both need the bearer
// token attached by hand, both answer the `apierr` envelope on failure, and
// both carry `X-Request-Id` on every response including successes (`OD-11`).
// Four conventions from `06` §1 are implemented here once rather than in
// fourteen domain modules:
//
//   - **Errors** are parsed out of the envelope into a typed [ApiError] that
//     carries `code`, `message` and `request_id` (§1.4).
//   - **Pagination is keyset**, never offset: `{items, next}` with an opaque
//     cursor, and `next` is null exactly on the last page (§1.5).
//   - **Idempotency keys** are client-generated and required on the six
//     expensive operations (§1.7); [newIdempotencyKey] mints them.
//   - **Optimistic concurrency** is `ETag` + `If-Match`, and the UI always
//     sends it (§1.7); [getWithETag] is how a form reads the tag it will send
//     back.

import { authHeader } from "./client.js";
import { ApiError, parseError } from "./errors.js";

/** The response header carrying the request id, mirroring
 * `apierr.HeaderRequestID`. Case-insensitive on read. */
const REQUEST_ID_HEADER = "X-Request-Id";

/**
 * RequestOptions is `RequestInit` with the headers narrowed to a plain string
 * map (so the helpers can merge them) plus the four wire conventions the API
 * defines above the HTTP layer.
 */
export interface RequestOptions extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>;
  /** `Idempotency-Key` (§1.7). Required on instance create/delete, publish,
   * rollback, API-key create and any job-creating proxied command. Mint it with
   * [newIdempotencyKey] once per user intent and reuse it across retries. */
  idempotencyKey?: string;
  /** `If-Match` (§1.7). A mismatch answers `412 precondition_failed` rather
   * than overwriting a colleague's edit. */
  ifMatch?: string;
  /** Single-use step-up grant, sent as `X-Verification` (`06` §2.5). */
  verification?: string;
  /** Send no `Authorization` header. For the genuinely anonymous routes —
   * invite lookup, the passkey login pair, key exchange — where a stale stored
   * token would produce a 401 unrelated to the request. */
  anonymous?: boolean;
}

/**
 * Page is the keyset envelope on every list route (`06` §1.5). There is no
 * `total`: counting a growing audit index on every page is a database stall the
 * single-write-connection model cannot absorb. `next` is null **exactly** on
 * the last page — feed it back as `cursor` to get the following one, and never
 * parse it, it is an opaque base64url of the sort tuple.
 */
export interface Page<T> {
  items: T[];
  next: string | null;
}

/** PageParams are the two pagination inputs every list route accepts. `limit`
 * defaults to 50 and caps at 200 (1000 on audit and analytics). */
export interface PageParams {
  limit?: number;
  cursor?: string;
}

/** QueryValue is what [query] can serialize. `undefined`, `null` and the empty
 * string are dropped, so an unset filter is simply absent from the URL. */
export type QueryValue = string | number | boolean | undefined | null;

/**
 * request performs a fetch against a same-origin API path and returns the raw
 * `Response` on success. Callers wanting JSON use [getJSON] / [sendJSON];
 * callers wanting bytes (a generation bundle, an audit export) use this.
 *
 * On a non-2xx it reads the `apierr` envelope and throws the typed [ApiError],
 * so no caller ever inspects `res.ok`.
 */
export async function request(path: string, opts: RequestOptions = {}): Promise<Response> {
  const { headers, idempotencyKey, ifMatch, verification, anonymous, ...init } = opts;
  const merged: Record<string, string> = {
    ...(anonymous ? {} : authHeader()),
    ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    ...(ifMatch ? { "If-Match": ifMatch } : {}),
    ...(verification ? { "X-Verification": verification } : {}),
    ...(headers ?? {}),
  };

  let res: Response;
  try {
    res = await fetch(path, { ...init, headers: merged });
  } catch (err) {
    // A fetch rejection is a transport failure: offline, DNS, CORS, or an
    // aborted request. There is no envelope and no request id to quote, and
    // calling it `internal` would blame the server for the user's tunnel.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({
      code: "internal",
      message: "Could not reach Sluice Cloud. Check your connection and try again.",
    });
  }

  lastId = res.headers.get(REQUEST_ID_HEADER) ?? lastId;
  if (!res.ok) throw await errorFromResponse(res);
  return res;
}

/** requestJSON is [request] plus a JSON body parse. A `204 No Content` — which
 * `06` §1.10 makes the answer to every synchronous `DELETE` — resolves to
 * `undefined`, so `Promise<void>` callers need no special case. */
export async function requestJSON<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await request(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** getJSON is a GET returning parsed JSON. */
export function getJSON<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return requestJSON<T>(path, { ...opts, method: "GET" });
}

/**
 * getWithETag is a GET that also returns the resource's `ETag`. Mutable
 * resources — instance, policy object, server config, alert rule — carry one,
 * and `06` §1.7 requires the UI to send it back as `If-Match` on the write. A
 * form that reads with [getJSON] cannot do that and will overwrite a
 * colleague's edit; this is the read to use on any screen that then saves.
 */
export async function getWithETag<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ value: T; etag: string }> {
  const res = await request(path, { ...opts, method: "GET" });
  const etag = res.headers.get("ETag") ?? "";
  return { value: (await res.json()) as T, etag };
}

/** sendJSON writes a JSON body with the given method and parses the response. */
export function sendJSON<T>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  return requestJSON<T>(path, {
    ...opts,
    method,
    headers: { "Content-Type": "application/json; charset=utf-8", ...(opts.headers ?? {}) },
    body: JSON.stringify(body),
  });
}

/**
 * del issues a DELETE. `06` §1.10 answers `204` for a synchronous delete and
 * `202` + a job for an asynchronous one; deleting an absent record is `404`,
 * not an idempotent 200, so a script can tell "removed" from "never existed".
 */
export async function del(path: string, opts: RequestOptions = {}): Promise<void> {
  await request(path, { ...opts, method: "DELETE" });
}

/**
 * query builds a `?a=b&c=d` string, dropping every unset value. An array value
 * repeats the parameter, which `06` §1.6 defines as OR within a dimension and
 * AND across dimensions:
 *
 *   query({ decision: ["deny", "reject"], subject: "analyst@acme.example" })
 *   // ?decision=deny&decision=reject&subject=analyst%40acme.example
 */
export function query(params: Record<string, QueryValue | QueryValue[]>): string {
  const q = new URLSearchParams();
  for (const [key, raw] of Object.entries(params)) {
    for (const v of Array.isArray(raw) ? raw : [raw]) {
      if (v === undefined || v === null || v === "") continue;
      q.append(key, String(v));
    }
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * newIdempotencyKey mints a client-side key for `Idempotency-Key` (`06` §1.7):
 * 26 Crockford base32 characters — a ULID, the form the spec recommends and the
 * same one `apierr.NewRequestID` mints server-side, so both correlation ids in
 * the product read alike. Time-ordered, so a support engineer scanning keys
 * sees them in the order they were issued.
 *
 * Mint it **once per user intent**, not once per attempt: the whole point is
 * that a client retry after a timeout replays the stored response instead of
 * provisioning a second instance.
 */
export function newIdempotencyKey(): string {
  const raw = new Uint8Array(16);
  // 48 bits of millisecond timestamp, big-endian. Plain arithmetic rather than
  // bit shifts: Date.now() exceeds 32 bits, which is where `<<` wraps.
  let ms = Date.now();
  for (let i = 5; i >= 0; i--) {
    raw[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  crypto.getRandomValues(raw.subarray(6));

  // 26 symbols carry 130 bits, so the 128-bit value is left-padded with two
  // zero bits — the standard ULID encoding.
  const alphabet = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
  let out = "";
  let acc = 0;
  let bits = 2;
  for (const b of raw) {
    acc = (acc << 8) | b;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += alphabet[(acc >>> bits) & 0x1f];
    }
    acc &= (1 << bits) - 1;
  }
  return out;
}

/**
 * lastRequestId returns the `X-Request-Id` of the most recent response, success
 * or failure. `OD-11` puts the id on every response so a reader can quote one
 * for a screen that rendered wrongly rather than only for a screen that errored;
 * a diagnostics footer is the intended consumer. For a failure prefer
 * `ApiError.requestId`, which is the id of *that* request rather than of
 * whatever raced after it.
 */
export function lastRequestId(): string {
  return lastId;
}

let lastId = "";

/** errorFromResponse reads the envelope off a non-2xx response. A body that is
 * missing or not JSON — a proxy's own error page, say — still yields a typed
 * error, with the request id taken from the header instead. */
async function errorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const err = parseError({ status: res.status, response: body });
  if (err.requestId) return err;
  const headerId = res.headers.get(REQUEST_ID_HEADER);
  if (!headerId) return err;
  return new ApiError({
    code: err.code,
    message: err.serverMessage,
    requestId: headerId,
    status: err.status,
    details: err.details,
  });
}
